import React, { useState } from 'react';
import { Check, Plus, Trash2, Eye, Save } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface Teilziel {
  text: string;
  bis_wann: string;
  erreicht: boolean;
}

interface Meilenstein {
  beschreibung: string;
  datum: string;
  erreicht: boolean;
}

interface ZieldefinitionData {
  hauptziel: string;
  smart_spezifisch: string;
  smart_messbar: string;
  smart_attraktiv: string;
  smart_realistisch: string;
  smart_terminiert: string;
  teilziele: Teilziel[];
  erfolgsindikatoren: string;
  meilensteine: Meilenstein[];
}

const defaultData: ZieldefinitionData = {
  hauptziel: '',
  smart_spezifisch: '',
  smart_messbar: '',
  smart_attraktiv: '',
  smart_realistisch: '',
  smart_terminiert: '',
  teilziele: [],
  erfolgsindikatoren: '',
  meilensteine: [],
};

interface Props {
  data: any;
  onSave: (data: any) => Promise<void>;
  blockId: string;
  clientEmail: string;
}

export function ZieldefinitionForm({ data, onSave, blockId, clientEmail }: Props) {
  const { showToast } = useToast();
  const [form, setForm] = useState<ZieldefinitionData>({ ...defaultData, ...data });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Teilziele
  const addTeilziel = () => {
    setForm({ ...form, teilziele: [...form.teilziele, { text: '', bis_wann: '', erreicht: false }] });
  };
  const updateTeilziel = (index: number, field: keyof Teilziel, value: string | boolean) => {
    const updated = [...form.teilziele];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, teilziele: updated });
  };
  const removeTeilziel = (index: number) => {
    setForm({ ...form, teilziele: form.teilziele.filter((_, i) => i !== index) });
  };

  // Meilensteine
  const addMeilenstein = () => {
    setForm({ ...form, meilensteine: [...form.meilensteine, { beschreibung: '', datum: '', erreicht: false }] });
  };
  const updateMeilenstein = (index: number, field: keyof Meilenstein, value: string | boolean) => {
    const updated = [...form.meilensteine];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, meilensteine: updated });
  };
  const removeMeilenstein = (index: number) => {
    setForm({ ...form, meilensteine: form.meilensteine.filter((_, i) => i !== index) });
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/blocks/${blockId}/export/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Zieldefinition.pdf';
      a.click();
      URL.revokeObjectURL(url);
      showToast('PDF heruntergeladen', 'success');
    } catch {
      showToast('Export fehlgeschlagen', 'error');
    }
  };

  const smartSections = [
    { letter: 'S', label: 'Spezifisch', name: 'smart_spezifisch', placeholder: 'Was genau soll erreicht werden?' },
    { letter: 'M', label: 'Messbar', name: 'smart_messbar', placeholder: 'Woran erkennen wir den Erfolg?' },
    { letter: 'A', label: 'Attraktiv', name: 'smart_attraktiv', placeholder: 'Warum ist dieses Ziel wichtig? Was ist die Motivation?' },
    { letter: 'R', label: 'Realistisch', name: 'smart_realistisch', placeholder: 'Ist das Ziel erreichbar? Welche Ressourcen sind nötig?' },
    { letter: 'T', label: 'Terminiert', name: 'smart_terminiert', placeholder: '' },
  ];

  return (
    <div className="block-form">
      <div className="block-form-section">
        <h4>Hauptziel</h4>
        <div className="form-group">
          <label>Das übergeordnete Coaching-Ziel</label>
          <textarea name="hauptziel" value={form.hauptziel} onChange={handleChange} placeholder="Was soll durch das Coaching erreicht werden?" />
        </div>
      </div>

      <div className="block-form-section">
        <h4>SMART-Kriterien</h4>
        {smartSections.map((s) => (
          <div className="smart-card" key={s.letter}>
            <div style={{ marginBottom: 8 }}>
              <span className="smart-letter">{s.letter}</span>
              <strong>{s.label}</strong>
            </div>
            {s.name === 'smart_terminiert' ? (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Bis wann soll das Ziel erreicht sein?</label>
                <input
                  type="date"
                  name={s.name}
                  value={(form as any)[s.name]}
                  onChange={handleChange}
                  style={{ maxWidth: 220 }}
                />
              </div>
            ) : (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <textarea
                  name={s.name}
                  value={(form as any)[s.name]}
                  onChange={handleChange}
                  placeholder={s.placeholder}
                  style={{ minHeight: 60 }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="block-form-section">
        <h4>Teilziele</h4>
        {form.teilziele.map((tz, i) => (
          <div className="dynamic-list-item" key={i}>
            <input
              type="text"
              value={tz.text}
              onChange={(e) => updateTeilziel(i, 'text', e.target.value)}
              placeholder="Teilziel..."
            />
            <input
              type="date"
              value={tz.bis_wann}
              onChange={(e) => updateTeilziel(i, 'bis_wann', e.target.value)}
              style={{ maxWidth: 160 }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
              <input
                type="checkbox"
                checked={tz.erreicht}
                onChange={(e) => updateTeilziel(i, 'erreicht', e.target.checked)}
              />
              Erreicht
            </label>
            <button className="btn-icon" onClick={() => removeTeilziel(i)} title="Entfernen">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button className="btn btn-sm btn-secondary" onClick={addTeilziel}>
          <Plus size={14} /> Teilziel hinzufügen
        </button>
      </div>

      <div className="block-form-section">
        <h4>Erfolgsindikatoren</h4>
        <div className="form-group">
          <label>Woran werden wir konkret merken, dass sich etwas verändert hat?</label>
          <textarea name="erfolgsindikatoren" value={form.erfolgsindikatoren} onChange={handleChange} placeholder="Konkrete, beobachtbare Veränderungen..." />
        </div>
      </div>

      <div className="block-form-section">
        <h4>Meilensteine</h4>
        {form.meilensteine.map((ms, i) => (
          <div className="dynamic-list-item" key={i}>
            <input
              type="text"
              value={ms.beschreibung}
              onChange={(e) => updateMeilenstein(i, 'beschreibung', e.target.value)}
              placeholder="Beschreibung..."
            />
            <input
              type="date"
              value={ms.datum}
              onChange={(e) => updateMeilenstein(i, 'datum', e.target.value)}
              style={{ maxWidth: 160 }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
              <input
                type="checkbox"
                checked={ms.erreicht}
                onChange={(e) => updateMeilenstein(i, 'erreicht', e.target.checked)}
              />
              Erreicht
            </label>
            <button className="btn-icon" onClick={() => removeMeilenstein(i)} title="Entfernen">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button className="btn btn-sm btn-secondary" onClick={addMeilenstein}>
          <Plus size={14} /> Meilenstein hinzufügen
        </button>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
        </button>
        <button className="btn btn-secondary" onClick={handleExport}>
          <Eye size={16} /> PDF-Vorschau
        </button>
        {saved && (
          <span className="save-indicator">
            <Check size={14} /> Gespeichert
          </span>
        )}
      </div>
    </div>
  );
}
